/**
 * Temporary UI kit registry — mirrors public/assets/ui/kit/manifest.json.
 * Swap PNGs under extracted/ (same keys) for final ROLLBREAK art.
 */

export type NineSliceMargins = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type KitAssetMeta = {
  key: string;
  textureKey: string;
  path: string;
  nineSlice: NineSliceMargins | null;
};

const EXT = 'assets/ui/kit/extracted';

function asset(
  key: string,
  nineSlice: NineSliceMargins | null = null,
): KitAssetMeta {
  return {
    key,
    textureKey: `ui-kit-${key}`,
    path: `${EXT}/${key}.png`,
    nineSlice,
  };
}

/** Priority chrome + supporting assets used by shell / Town / Hero / Talents. */
export const KIT_ASSETS: readonly KitAssetMeta[] = [
  asset('panel-gold-ornate', { left: 28, right: 28, top: 28, bottom: 28 }),
  asset('panel-parchment', { left: 8, right: 8, top: 8, bottom: 8 }),
  asset('panel-stone-a', { left: 10, right: 10, top: 10, bottom: 10 }),
  asset('panel-stone-b', { left: 10, right: 10, top: 10, bottom: 10 }),
  asset('panel-parchment-sm', { left: 6, right: 6, top: 6, bottom: 6 }),
  asset('btn-wide-normal', { left: 14, right: 14, top: 4, bottom: 4 }),
  asset('btn-wide-alt', { left: 14, right: 14, top: 4, bottom: 4 }),
  asset('btn-wide-selected-body', { left: 14, right: 14, top: 4, bottom: 4 }),
  asset('slot-empty-a', { left: 6, right: 6, top: 6, bottom: 6 }),
  asset('slot-empty-b', { left: 6, right: 6, top: 6, bottom: 6 }),
  asset('slot-selected-body', { left: 6, right: 6, top: 4, bottom: 4 }),
  asset('tab-normal', { left: 6, right: 6, top: 3, bottom: 3 }),
  asset('tab-disabled', { left: 6, right: 6, top: 3, bottom: 3 }),
  asset('tab-selected-body', { left: 6, right: 6, top: 3, bottom: 3 }),
  asset('bar-frame-a', { left: 10, right: 10, top: 1, bottom: 1 }),
  asset('bar-frame-b', { left: 10, right: 10, top: 1, bottom: 1 }),
  asset('bar-frame-c', { left: 10, right: 10, top: 1, bottom: 1 }),
  asset('bar-nameplate-body', { left: 10, right: 10, top: 2, bottom: 2 }),
  asset('bar-fill-blue'),
  asset('bar-fill-red'),
  asset('bar-fill-green'),
  asset('overlay-crest-wide'),
  asset('overlay-crest-tab'),
  asset('overlay-crest-slot'),
  asset('overlay-crest-nameplate'),
  asset('icon-close'),
  asset('icon-alert'),
  asset('marker-gold-0'),
  asset('marker-grey-0'),
  asset('divider-diamond-a'),
];

const BY_KEY = new Map(KIT_ASSETS.map((a) => [a.key, a]));

export function kitMeta(key: string): KitAssetMeta | undefined {
  return BY_KEY.get(key);
}

export function kitTextureKey(key: string): string {
  return BY_KEY.get(key)?.textureKey ?? `ui-kit-${key}`;
}

export const KIT_STATES = {
  button: {
    normal: 'btn-wide-normal',
    alt: 'btn-wide-alt',
    selected: 'btn-wide-selected-body',
    selectedOverlay: 'overlay-crest-wide',
    disabled: 'btn-wide-alt',
  },
  tab: {
    normal: 'tab-normal',
    selected: 'tab-selected-body',
    selectedOverlay: 'overlay-crest-tab',
    disabled: 'tab-disabled',
  },
  slot: {
    empty: 'slot-empty-a',
    normal: 'slot-empty-b',
    selected: 'slot-selected-body',
    selectedOverlay: 'overlay-crest-slot',
    equipped: 'slot-selected-body',
    equippedOverlay: 'overlay-crest-slot',
    disabled: 'slot-empty-a',
  },
  panel: {
    ornate: 'panel-gold-ornate',
    parchment: 'panel-parchment',
    stone: 'panel-stone-a',
    stoneDark: 'panel-stone-b',
    parchmentSm: 'panel-parchment-sm',
  },
  progress: {
    frame: 'bar-frame-a',
    fillBlue: 'bar-fill-blue',
    fillRed: 'bar-fill-red',
    fillGreen: 'bar-fill-green',
  },
  nameplate: {
    body: 'bar-nameplate-body',
    crest: 'overlay-crest-nameplate',
  },
} as const;
